<template>
  <div class="search-chart__container" />
</template>

<script>
import { useI18n } from 'vue-i18n'

export default {
  name: 'SearchChart',
  props: {
    data: {
      type: Object,
      required: true
    },
    startYear: {
      type: Number,
      default: null
    },
    endYear: {
      type: Number,
      default: null
    }
  },
  setup() {
    const { t } = useI18n({
      fallbackWarn: false,
      silentTranslationWarn: true,
      silentFallbackWarn: true,
      messages: {
        pt: {
          tooltip: {
            xAxis: 'Ano:',
            yAxis: 'Registros:'
          }
        },
        en: {
          tooltip: {
            xAxis: 'Year:',
            yAxis: 'Records:'
          }
        }
      }
    })
    return { t }
  },
  data() {
    return {
      isFirstRender: true
    }
  },
  computed: {
    // Compute years with default values if null
    computedStartYear() {
      if (this.startYear !== null && this.startYear !== undefined) {
        return this.startYear
      }
      return new Date().getFullYear() - 20 // Default: 20 years ago
    },
    computedEndYear() {
      if (this.endYear !== null && this.endYear !== undefined) {
        return this.endYear
      }
      return new Date().getFullYear() // Default: current year
    },
    // Transform Elasticsearch aggregations data to chart format
    chartData() {
      const yearRange = this.generateYearRange()

      // Create data for all years in range, using doc_count from aggregations
      return yearRange.map((year) => {
        const yearKey = year.toString()
        const aggregationData = this.data[yearKey]

        return {
          year: year,
          totalRecords: aggregationData ? aggregationData.doc_count : 0
        }
      })
    },
    // Get years for x-axis categories
    years() {
      return this.chartData.map(d => d.year)
    },
    // Get record counts for series data
    recordCounts() {
      return this.chartData.map(d => d.totalRecords)
    }
  },
  watch: {
    data() {
      this.renderChart()
    },
    startYear() {
      this.renderChart()
    },
    endYear() {
      this.renderChart()
    },
    '$i18n.locale'() {
      this.renderChart()
    }
  },
  mounted() {
    this.renderChart()
    const resizeObserver = new ResizeObserver(() => {
      this.renderChart()
    })
    resizeObserver.observe(this.$el)
  },
  methods: {
    // Generate array of years from computedStartYear to computedEndYear
    generateYearRange() {
      const years = []
      for (let year = this.computedStartYear; year <= this.computedEndYear; year++) {
        years.push(year)
      }
      return years
    },

    async renderChart() {
      const HighCharts = await import('highcharts')
      this.chart = HighCharts.chart(this.$el, {
        chart: {
          type: 'column',
          spacingLeft: 0,
          spacingRight: 0,
          spacingBottom: 0
        },
        title: false,
        credits: false,
        legend: false,
        xAxis: {
          categories: this.years,
          lineWidth: 0,
          labels: {
            enabled: false
          },
          tickLength: 0
        },
        yAxis: {
          title: false,
          min: 0,
          max: Math.max(...this.recordCounts),
          labels: {
            enabled: false
          },
          gridLineWidth: 0
        },
        plotOptions: {
          series: {
            borderWidth: 0,
            pointPadding: 0.05,
            groupPadding: 0.15,
            animation: false,
            color: {
              linearGradient: { x1: 0, x2: 0, y1: 1, y2: 0 },
              stops: [
                [0, '#019ae0'],
                [1, '#435bd9']
              ]
            }
          }
        },
        series: [
          {
            name: 'Records',
            data: this.recordCounts
          }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@use "#src/assets/scss/variables" as *;

.search-chart__container {
  height: 205px;
  width: 100%;
  padding-bottom: 10px;
}

.search-chart__container:deep(svg) {
  rect.bar {
    fill: $light-blue;
    stroke: $light-blue-700;
  }

  .axis {
    .domain {
      stroke: $light-blue-700;
    }
  }
  .axis__label--x {
    text {
      fill: $gray;
      font-size: 11px;
    }
  }
}
</style>
